// <---- Core Imports ---->
import { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

// <---- Context and Provider Imports ---->
import { PurchasesProvider } from "@/context/purchases";
import { SocketProvider } from "@/providers/socket.provider";

// <---- Redux Store Imports ---->
import { useAppSelector } from "@/hooks/redux";
import { Loading } from "./components/ui/loading";
import SendRequestWithoutJobPostPage from "./pages/dashboard/employer/VerificationChecks/SendRequestWithoutJobPost";
import Jobseeker from "@/pages/dashboard/jobseeker/Jobs";
import JobSeekerVerificationApplicationDetail from "./pages/dashboard/jobseeker/VerificationChecks/ApplicationDetail";

// <---- Lazy Imports ---->
const Subscription = lazy(() => import("@/pages/subscription"));
const ProtectedRoutes = lazy(() => import("@/routes/ProtectedRoutes"));
const RedirectRoutes = lazy(() => import("@/routes/RedirectRoutes"));
const NoInternet = lazy(() => import("@/pages/error/NoInternet"));
const NotFound = lazy(() => import("@/pages/error/NotFound"));

const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const Login = lazy(() => import("@/pages/auth/Login"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const SignUp = lazy(() => import("@/pages/auth/Signup"));
const JobSeekerAddress = lazy(() => import("@/pages/onboarding/Address"));
const VerifyEmail = lazy(() => import("@/pages/onboarding/VerifyEmail"));
const VerifyTaxID = lazy(() => import("@/pages/onboarding/VerifyTaxID"));
const JobSeekerReferrals = lazy(() => import("@/pages/onboarding/Referrals/JobSeekerReferrals"));
const EmployerReferrals = lazy(() => import("@/pages/onboarding/Referrals/EmployerReferrals"));

const Chats = lazy(() => import("@/pages/common/Chats"));
const ChangeEmail = lazy(() => import("@/pages/common/Account/Email"));
const ChangePassword = lazy(() => import("@/pages/common/Account/Password"));
const PersonalInfo = lazy(() => import("@/pages/common/Account/PersonalInfo"));
const ContactUs = lazy(() => import("@/pages/common/ContactUs"));
const FAQs = lazy(() => import("@/pages/common/FAQs"));
const HelpCenter = lazy(() => import("@/pages/common/HelpCenter"));
const PrivacyPolicy = lazy(() => import("@/pages/common/PrivacyPolicy"));
const Settings = lazy(() => import("@/pages/common/Settings"));
const TermsAndConditions = lazy(() => import("@/pages/common/TermsAndConditions"));

const EmployerRootLayout = lazy(() => import("@/layouts/employer"));
const EmployerAccountLayout = lazy(() => import("@/layouts/employer/Account"));
const JobseekerRootLayout = lazy(() => import("@/layouts/jobseeker"));
const JobseekerAccountLayout = lazy(() => import("@/layouts/jobseeker/Account"));
const JobseekerProfileLayout = lazy(() => import("@/layouts/jobseeker/Profile"));

const EmployerHome = lazy(() => import("@/pages/dashboard/employer/Home"));
const CandidateDetails = lazy(() => import("@/pages/dashboard/employer/Home/CandidateDetails"));
const CandidateSearch = lazy(() => import("@/pages/dashboard/employer/Home/CandidateSearch"));
const ApplicationDetail = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/ApplicationDetail"),
);
const PostJob = lazy(() => import("@/pages/dashboard/employer/Jobs/PostJob"));
const EmployerProfile = lazy(() => import("@/pages/dashboard/employer/Profile"));
const CreateInterview = lazy(
  () => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews/Create"),
);
const EditInterview = lazy(() => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews/Edit"));
const Jobs = lazy(() => import("@/pages/dashboard/employer/Jobs"));
const Applications = lazy(() => import("@/pages/dashboard/employer/Jobs/Applications"));
const ArchivedApplications = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/Archived"),
);
const ShortlistedApplications = lazy(
  () => import("@/pages/dashboard/employer/Jobs/Applications/Shortlisted"),
);
const EditJob = lazy(() => import("@/pages/dashboard/employer/Jobs/EditJob"));
const JobDetail = lazy(() => import("@/pages/dashboard/employer/Jobs/JobDetail"));
const StarredCandidates = lazy(() => import("@/pages/dashboard/employer/StarredCandidates"));
const RequestedCandidate = lazy(() => import("@/pages/dashboard/employer/Jobs/RequestedCandidate"));
const EmployerScheduledInterviews = lazy(
  () => import("@/pages/dashboard/employer/Jobs/ScheduleInterviews"),
);

const Interview = lazy(() => import("@/pages/dashboard/jobseeker/Interviews"));
const ZoomMeet = lazy(() => import("@/pages/dashboard/jobseeker/Interviews/Zoom"));
const JobseekerHome = lazy(() => import("@/pages/dashboard/jobseeker/Jobs"));
const AppliedJobs = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Applied"));
const ApplyJob = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Apply"));
const JobsDetails = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Details"));
const SavedJobs = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Saved"));
const ReviewApplication = lazy(() => import("@/pages/dashboard/jobseeker/Jobs/Review"));
const JobseekerJobDetails = lazy(() => import("@/pages/dashboard/jobseeker/Profile/JobDetails"));
const JobseekerPersonalInformation = lazy(
  () => import("@/pages/dashboard/jobseeker/Profile/PersonalInformation"),
);
const JobseekerPortfolio = lazy(() => import("@/pages/dashboard/jobseeker/Profile/Portfolio"));
const JobseekerProfessionalDetails = lazy(
  () => import("@/pages/dashboard/jobseeker/Profile/ProfessionalDetails"),
);
const JobseekerEmployerProfile = lazy(() => import("@/pages/dashboard/jobseeker/Profile/Employer"));

const ZoomMeeting = lazy(() => import("@/pages/dashboard/ZoomMeeting/ZoomMeeting"));
const VerficationChecksPricing = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Pricing"),
);
const VerificationApplicationForm = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Application"),
);
const BackgroundChecksDashboard = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/Dashboard"),
);
const BackgroundApplicationDetail = lazy(
  () => import("./pages/dashboard/jobseeker/VerificationChecks/ReportDetail"),
);
const EmployerVerficationChecksPricing = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/Pricing"),
);
const VerificationChecksLayout = lazy(() => import("./layouts/jobseeker/VerificationChecks"));
const SendRequestPage = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/SendRequests"),
);
const BackgroundChecksOrganizationDashboard = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/Dashboard"),
);
const BackgroundReportDetail = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/ReportDetail"),
);
const VerificationApplicationDetail = lazy(
  () => import("./pages/dashboard/employer/VerificationChecks/ApplicationDetail"),
);

function App() {
  const user = useAppSelector((state) => state?.auth?.userData);
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);

  useEffect(() => {
    const handleNetworkChange = () => {
      setIsOnline(window.navigator.onLine);
    };

    window.addEventListener("online", handleNetworkChange);
    window.addEventListener("offline", handleNetworkChange);

    return () => {
      window.removeEventListener("online", handleNetworkChange);
      window.removeEventListener("offline", handleNetworkChange);
    };
  }, []);

  if (!isOnline) {
    return <NoInternet />;
  }

  return (
    <SocketProvider
      existingRefreshToken={user && user.refresh_token ? user.refresh_token : ""}
      initialAccessToken={user && user.access_token ? user.access_token : ""}
    >
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* <-------- PASSWORD RECOVERY AND VERIFICATION AND ONBOARDING PAGES --------> */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-tax-id" element={<VerifyTaxID />} />
          <Route path="/signup/address" element={<JobSeekerAddress />} />
          <Route path="/signup/user-referrals" element={<JobSeekerReferrals />} />
          <Route path="/signup/referrals" element={<EmployerReferrals />} />

          {/* <-------- AUTH PAGES --------> */}
          <Route element={<RedirectRoutes />}>
            <Route index path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
          </Route>

          {/* <-------- EMPLOYER DASHBOARD --------> */}
          <Route element={<ProtectedRoutes roles={["employer"]} />}>
            <Route
              path="/employer"
              element={
                <PurchasesProvider>
                  <EmployerRootLayout />
                </PurchasesProvider>
              }
            >
              <Route index element={<EmployerHome />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="jobs/:id/edit" element={<EditJob />} />
              <Route path="jobs/:id/applications" element={<Applications />} />
              <Route
                path="jobs/:id/applications/shortlisted"
                element={<ShortlistedApplications />}
              />
              <Route path="jobs/:id/applications/archived" element={<ArchivedApplications />} />
              <Route path="jobs/:id/applications/:applicationId" element={<ApplicationDetail />} />
              <Route path="starred-candidates" element={<StarredCandidates />} />
              <Route path="requested-candidates" element={<RequestedCandidate />} />
              <Route path="scheduled-interviews" element={<EmployerScheduledInterviews />} />
              {/* TODO: Check below route */}
              <Route path="scheduled-interviews/zoom" element={<ZoomMeeting />} />
              <Route path="interviews/:id/edit" element={<EditInterview />} />
              <Route path="applications/:id/create-interview" element={<CreateInterview />} />

              <Route path="jobseekers" element={<CandidateSearch />} />
              <Route path="jobseekers/:id" element={<CandidateDetails />} />

              <Route path="post-job" element={<PostJob />} />

              {/* <--- Chat Pages ---> */}
              <Route path="chats" element={<Chats />} />
              <Route path="chats/:id" element={<Chats />} />

              {/* <--- Profile Pages ---> */}
              <Route path="profile" element={<EmployerProfile />} />

              {/* <-------- COMMON PAGES --------> */}
              <Route path="account" element={<EmployerAccountLayout />}>
                <Route path="personal-info" element={<PersonalInfo />} />
                <Route path="change-email" element={<ChangeEmail />} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="settings" element={<Settings />} />
              <Route path="verification-checks" element={<VerificationChecksLayout />}>
                <Route index element={<Navigate to="pricing" replace />} />
                <Route path="pricing" element={<EmployerVerficationChecksPricing />} />
                <Route path="pricing/:id" element={<SendRequestPage />} />
                <Route
                  path="pricing/without-job-post/:id"
                  element={<SendRequestWithoutJobPostPage />}
                />
                <Route path="dashboard" element={<BackgroundChecksOrganizationDashboard />} />
                <Route path="dashboard/:id" element={<BackgroundReportDetail />} />
                <Route path="applications/:id" element={<VerificationApplicationDetail />} />
              </Route>
              {/* <-------- SUBSCRIPTION PAGES --------> */}
              <Route path="subscription" element={<Subscription />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* <-------- JOBSEEKER DASHBOARD --------> */}
          <Route element={<ProtectedRoutes roles={["job_seeker"]} />}>
            <Route path="/jobseeker" element={<JobseekerRootLayout />}>
              {/* <--- Jobs Pages ---> */}
              <Route index element={<JobseekerHome />} />
              <Route path="jobs/applied" element={<AppliedJobs />} />
              <Route path="jobs/saved" element={<SavedJobs />} />
              <Route path="jobs/detail" element={<JobsDetails />} />
              <Route path="jobs/apply" element={<ApplyJob />} />
              <Route path="jobs/review" element={<ReviewApplication />} />
              {/* <--- Interview Pages ---> */}
              <Route path="interviews" element={<Interview />} />
              <Route path="interviews/zoom" element={<ZoomMeet />} />
              {/* <--- Chat Pages ---> */}
              <Route path="chats" element={<Chats />} />
              <Route path="chats/:id" element={<Chats />} />
              {/* <--- Profile Pages ---> */}
              <Route path="profile" element={<JobseekerProfileLayout />}>
                <Route path="personal-information" element={<JobseekerPersonalInformation />} />
                <Route path="portfolio" element={<JobseekerPortfolio />} />
                <Route path="job-details" element={<JobseekerJobDetails />} />
                <Route path="professional-details" element={<JobseekerProfessionalDetails />} />
              </Route>
              <Route path="employers/:id" element={<JobseekerEmployerProfile />} />
              {/* <-------- COMMON PAGES --------> */}
              <Route path="account" element={<JobseekerAccountLayout />}>
                <Route path="personal-info" element={<PersonalInfo />} />
                <Route path="change-email" element={<ChangeEmail />} />
                <Route path="change-password" element={<ChangePassword />} />
              </Route>
              {/* <-------- VERIFICATION CHECKS PAGES --------> */}
              <Route path="verification-checks" element={<VerificationChecksLayout />}>
                <Route index element={<Navigate to="pricing" replace />} />
                <Route path="pricing" element={<VerficationChecksPricing />} />
                <Route path="pricing/:id" element={<VerificationApplicationForm />} />
                <Route path="applications/:id" element={<JobSeekerVerificationApplicationDetail />} />
                <Route path="dashboard" element={<BackgroundChecksDashboard />} />
                <Route path="dashboard/:id" element={<BackgroundApplicationDetail />} />
              </Route>
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </SocketProvider>
  );
}

export default App;
